<script setup lang="ts">
const props = defineProps<{
  transparent?: boolean
  block?: boolean
}>()

const { locale, t } = useI18n()
const {
  mode,
  booking,
  daysAgo,
  hasBookingWidget,
  repeatUrl,
  bookingsLink
} = useBookingWidget()

const hotel = computed(() => booking.value?.hotel)
const hotelImageSrc = computed(() => generateImgUrl(hotel.value?.image?.lightbox ?? ''))

const checkinDate = computed(() => booking.value?.date?.split('-').join('/') ?? '')
const checkinHour = computed(() =>
  booking.value?.hour ? formatDisplayHour(booking.value.hour, locale.value) : ''
)

const widgetLink = computed(() => {
  if (mode.value === 'repeat') return repeatUrl.value ?? bookingsLink.value
  return bookingsLink.value
})

const widgetTitle = computed(() => {
  if (mode.value === 'repeat') return t('labels.proximaReserva.repetir')
  return t('labels.proximaReserva.titulo')
})

const widgetSubtitle = computed(() => {
  if (mode.value === 'repeat') {
    if (daysAgo.value === 0) return t('labels.proximaReserva.repetirSubtituloHoy')
    if (daysAgo.value === 1) return t('labels.proximaReserva.repetirSubtituloAyer')
    if (daysAgo.value !== null) {
      return t('labels.proximaReserva.repetirSubtitulo', { days: daysAgo.value })
    }
    return hotel.value?.name ?? ''
  }

  return [checkinHour.value, checkinDate.value].filter(Boolean).join(' · ')
})
</script>

<template>
  <UButton
    v-if="hasBookingWidget"
    :to="widgetLink"
    :block="props.block"
    size="xs"
    :color="props.transparent ? 'primary' : 'neutral'"
    :variant="props.transparent ? 'outline' : 'subtle'"
    class="w-full justify-start sm:w-fit"
    :class="props.transparent ? 'text-white ring-white/40' : undefined"
    :ui="{
      base: props.block ? 'justify-start py-2.5' : 'gap-1.5'
    }"
  >
    <template #leading>
      <UAvatar
        :src="hotelImageSrc || undefined"
        :alt="hotel?.name ?? ''"
        icon="i-lucide-bed-double"
        size="sm"
        :ui="{ image: 'object-cover' }"
      />
    </template>
    <span class="flex flex-col items-start leading-tight text-left min-w-0">
      <span class="font-medium">{{ widgetTitle }}</span>
      <span
        v-if="widgetSubtitle"
        class="text-[11px] font-normal opacity-80 truncate max-w-[160px]"
      >
        {{ widgetSubtitle }}
      </span>
    </span>
  </UButton>
</template>
